import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  DoorOpen, Search, Calendar, Briefcase, Building2,
  FileSignature, Bell, ChevronDown, ChevronUp, AlertTriangle
} from 'lucide-react';
import api from '../services/api';

const SortiesManager = () => {
  const [sorties, setSorties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    loadSorties();
  }, []);

  const loadSorties = async () => {
    try {
      const res = await api.get('/sorties-volontaires');
      setSorties(res.data || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSorties = sorties.filter(s => 
    s.apprenant_nom?.toLowerCase().includes(search.toLowerCase()) ||
    s.apprenant_prenom?.toLowerCase().includes(search.toLowerCase()) ||
    s.motif?.toLowerCase().includes(search.toLowerCase())
  );

  const getMotifIcon = (motif) => {
    if (motif?.includes('Emploi') || motif?.includes('Intérim')) return <Briefcase className="w-4 h-4" />;
    if (motif?.includes('entreprise')) return <Building2 className="w-4 h-4" />;
    return <DoorOpen className="w-4 h-4" />;
  };

  const getMotifColor = (motif) => {
    if (motif?.includes('Emploi') || motif?.includes('Intérim')) return 'bg-emerald-100 text-emerald-700';
    if (motif?.includes('entreprise')) return 'bg-blue-100 text-blue-700';
    if (motif?.includes('Formation')) return 'bg-purple-100 text-purple-700';
    if (motif?.includes('Abandon')) return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-700';
  };

  const stats = {
    total: sorties.length,
    emploi: sorties.filter(s => s.motif?.includes('Emploi') || s.motif?.includes('Intérim')).length,
    creation: sorties.filter(s => s.motif?.includes('entreprise')).length,
    formation: sorties.filter(s => s.motif?.includes('Formation')).length,
    abandon: sorties.filter(s => s.motif?.includes('Abandon')).length
  };

  if (loading) {
    return (
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-500">Chargement...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600">Total sorties</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <DoorOpen className="w-6 h-6 text-amber-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600">Emploi/Intérim</p>
                <p className="text-2xl font-bold text-emerald-700">{stats.emploi}</p>
              </div>
              <Briefcase className="w-6 h-6 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600">Création</p>
                <p className="text-2xl font-bold text-blue-700">{stats.creation}</p>
              </div>
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-fuchsia-50 rounded-3xl border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600">Formation</p>
                <p className="text-2xl font-bold text-purple-700">{stats.formation}</p>
              </div>
              <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-rose-50 rounded-3xl border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600">Abandon</p>
                <p className="text-2xl font-bold text-red-700">{stats.abandon}</p>
              </div>
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DoorOpen className="w-5 h-5 text-amber-600" />
              Demandes de Sortie Volontaire
            </CardTitle>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="pl-10 rounded-xl w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredSorties.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <DoorOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucune demande de sortie volontaire</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredSorties.map((sortie) => (
                <div key={sortie.id} className="p-4">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedId(expandedId === sortie.id ? null : sortie.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-semibold">
                        {sortie.apprenant_prenom?.[0]}{sortie.apprenant_nom?.[0]}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {sortie.apprenant_prenom} {sortie.apprenant_nom}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Calendar className="w-3 h-3" />
                          Sortie le {sortie.date_sortie}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <Badge className={getMotifColor(sortie.motif)}>
                        {getMotifIcon(sortie.motif)}
                        <span className="ml-1">{sortie.motif}</span>
                      </Badge>
                      
                      {sortie.notifications_envoyees && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <Bell className="w-3 h-3 mr-1" />
                          Notifié
                        </Badge>
                      )}
                      
                      {sortie.signature && (
                        <Badge className="bg-blue-100 text-blue-700">
                          <FileSignature className="w-3 h-3 mr-1" />
                          Signé
                        </Badge>
                      )}
                      
                      {expandedId === sortie.id ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                  
                  {expandedId === sortie.id && (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-sm text-slate-500">Date de la demande</p>
                          <p className="font-medium text-slate-900">
                            {new Date(sortie.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                        </div>
                        
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-sm text-slate-500">Date de sortie souhaitée</p>
                          <p className="font-medium text-slate-900">{sortie.date_sortie}</p>
                        </div>
                        
                        {sortie.entreprise_siret && (
                          <div className="bg-blue-50 rounded-xl p-4">
                            <p className="text-sm text-blue-600">SIRET de l'entreprise</p>
                            <p className="font-mono font-medium text-blue-900">{sortie.entreprise_siret}</p>
                          </div>
                        )}
                        
                        {sortie.entreprise_date_creation && (
                          <div className="bg-blue-50 rounded-xl p-4">
                            <p className="text-sm text-blue-600">Date de création entreprise</p>
                            <p className="font-medium text-blue-900">{sortie.entreprise_date_creation}</p>
                          </div>
                        )}
                      </div>
                      
                      {sortie.commentaire && (
                        <div className="bg-amber-50 rounded-xl p-4">
                          <p className="text-sm font-medium text-amber-700 mb-1">Commentaire</p>
                          <p className="text-amber-600">{sortie.commentaire}</p>
                        </div>
                      )}
                      
                      {sortie.signature && (
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-sm font-medium text-slate-700 mb-2">Signature électronique</p>
                          <img 
                            src={sortie.signature} 
                            alt="Signature" 
                            className="max-h-24 border border-slate-200 rounded-lg bg-white"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SortiesManager;
